import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CONSUMER_PAYMENT_SCHEDULES, DEFAULT_CONSUMER_PAYMENT_SCHEDULE } from '@shared/consumerPlan';
import { ChoiceList } from '../components/choice-list';
import { StepFrame } from '../components/step-frame';
import { useConsumerFlow } from '../hooks/use-consumer-flow';
import { useConsumerPlan } from '../hooks/use-consumer-plan';
import { COVERAGE_LANE_COPY } from '../lib/copy';
import { isGuidedJourney } from '../lib/guided-journey';
import { guidedProjectionPath } from '../lib/paths';
import { rememberSearchOrigin } from '../lib/search-origin';
import type { ConsumerStepProps } from './step-props';

/**
 * Reached from a self-funding coverage projection, so Back returns there: guided planning's for a
 * consumer it handed over, otherwise the consumer flow's own.
 */
export function PaymentPreferenceStep({ clientId }: ConsumerStepProps) {
  const { goTo, back } = useConsumerFlow(clientId);
  const navigate = useNavigate();
  const { plan, updatePlan } = useConsumerPlan(clientId);
  const [schedule, setSchedule] = useState<ConsumerPaymentSchedule>(
    plan?.paymentSchedule ?? DEFAULT_CONSUMER_PAYMENT_SCHEDULE,
  );

  const handleContinue = () => {
    updatePlan({ paymentSchedule: schedule });
    rememberSearchOrigin(clientId, 'payment-preference');
    goTo('searching');
  };

  return (
    <StepFrame
      step="payment-preference"
      title="How would you like to pay for coverage?"
      subtitle="We’ll show three options that fit how you’d like to pay. You can switch any time."
      onBack={() =>
        isGuidedJourney(clientId) ? navigate(guidedProjectionPath(clientId)) : back('assessment-projection')
      }
      primary={{ label: 'Show my options', onClick: handleContinue }}
      next="find the options that pay the most toward your care."
    >
      <ChoiceList
        label="How you’d like to pay"
        value={schedule}
        onChange={setSchedule}
        choices={CONSUMER_PAYMENT_SCHEDULES.map(value => ({
          value,
          title: COVERAGE_LANE_COPY[value].title,
          description: COVERAGE_LANE_COPY[value].description,
          badge: value === DEFAULT_CONSUMER_PAYMENT_SCHEDULE ? 'Most popular' : undefined,
        }))}
      />
    </StepFrame>
  );
}
